/**
 * Dispatcher Control Service (frontend)
 * AIDLC Administrative Backend
 *
 * Thin wrapper over the /api/dispatcher endpoints.
 */

import apiClient from './api';
import type {
  DispatcherStatus,
  DispatcherConfig,
  DispatcherLogEntry,
  DispatcherActionResult,
  DispatcherCommandResult,
} from '../types/dispatcher';

/**
 * The reason the server gave for refusing, when it gave one. A refusal arrives
 * as an HTTP error, so the reason is on the error's response, not in a result.
 */
export function dispatcherErrorReason(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { reason?: unknown; error?: unknown } } } | null)?.response?.data;
  if (typeof data?.reason === 'string' && data.reason) return data.reason;
  if (typeof data?.error === 'string' && data.error) return data.error;
  return fallback;
}

export async function getDispatcherState(): Promise<DispatcherStatus> {
  const { data } = await apiClient.get<DispatcherStatus>('/dispatcher/state');
  return data;
}

export async function getDispatcherLogs(limit = 200): Promise<DispatcherLogEntry[]> {
  const { data } = await apiClient.get<DispatcherLogEntry[]>('/dispatcher/logs', { params: { limit } });
  return data;
}

/**
 * Every change is sent with a JSON body, even an empty one. The server refuses a
 * dispatcher change that is not declared as application/json, because that is
 * what stops another site from posting one through the reader's browser.
 */
const NO_BODY = {};

/**
 * Stop waits for the dispatcher to end its sessions, see them gone and exit
 * (up to 20 s), then for a forced end to take effect (5 s); restart is that plus
 * a start (5 s). The shared client gives up after 15 s, which would report a stop
 * that is still under way as a failure.
 */
const SLOW_ACTION_TIMEOUT_MS = 40000;

export async function startDispatcher(): Promise<DispatcherActionResult> {
  const { data } = await apiClient.post<DispatcherActionResult>('/dispatcher/start', NO_BODY);
  return data;
}

export async function stopDispatcher(): Promise<DispatcherActionResult> {
  const { data } = await apiClient.post<DispatcherActionResult>('/dispatcher/stop', NO_BODY, { timeout: SLOW_ACTION_TIMEOUT_MS });
  return data;
}

export async function restartDispatcher(): Promise<DispatcherActionResult> {
  const { data } = await apiClient.post<DispatcherActionResult>('/dispatcher/restart', NO_BODY, { timeout: SLOW_ACTION_TIMEOUT_MS });
  return data;
}

export async function getDispatcherConfig(): Promise<DispatcherConfig> {
  const { data } = await apiClient.get<DispatcherConfig>('/dispatcher/config');
  return data;
}

export async function updateDispatcherConfig(config: Partial<DispatcherConfig>): Promise<DispatcherConfig> {
  const { data } = await apiClient.put<DispatcherConfig>('/dispatcher/config', config);
  return data;
}

/**
 * A kill is answered only when the dispatcher has seen the session's processes
 * gone, or has given up trying; the server waits up to 20 s for that.
 */
const KILL_TIMEOUT_MS = 30000;

export async function killDispatcherAgent(agentId: string): Promise<DispatcherCommandResult> {
  const { data } = await apiClient.post<DispatcherCommandResult>(`/dispatcher/agents/${encodeURIComponent(agentId)}/kill`, NO_BODY, { timeout: KILL_TIMEOUT_MS });
  return data;
}

export async function clearDispatcherQueue(): Promise<DispatcherCommandResult> {
  const { data } = await apiClient.post<DispatcherCommandResult>('/dispatcher/queue/clear', NO_BODY);
  return data;
}
